export const MAX_PARTICLES = 96;
const BINS = 4;

export class ParticleSystem {
  px: Float64Array;
  py: Float64Array;
  vx: Float64Array;
  vy: Float64Array;
  life: Float64Array;
  maxLife: Float64Array;
  size: Float64Array;
  count: number;
  /** Cuántas partículas caben. El anillo gira sobre este número, no sobre uno global. */
  readonly capacity: number;
  private binColors: string[];

  /**
   * `rgb` es el color sin envolver (`"255,46,99"`), como en `TrailSystem`.
   *
   * Color y capacidad son de la INSTANCIA y no del módulo porque hay más de un
   * surtidor: las chispas de patada son blancas y comparten un depósito grande;
   * los cascotes de un gol van del color del equipo y necesitan el suyo. Con un
   * solo depósito compartido, una explosión de gol se comería las chispas de
   * patada que hubiera en el aire — y saldrían del color equivocado.
   */
  constructor(rgb = "255,255,255", capacity = MAX_PARTICLES, maxAlpha = 0.8) {
    this.capacity = Math.max(1, capacity | 0);
    this.px = new Float64Array(this.capacity);
    this.py = new Float64Array(this.capacity);
    this.vx = new Float64Array(this.capacity);
    this.vy = new Float64Array(this.capacity);
    this.life = new Float64Array(this.capacity);
    this.maxLife = new Float64Array(this.capacity);
    this.size = new Float64Array(this.capacity);
    this.count = 0;
    this.binColors = new Array(BINS);
    for (let bin = 0; bin < BINS; bin++) {
      const alpha = ((bin + 0.5) / BINS) * maxAlpha;
      this.binColors[bin] = `rgba(${rgb},${alpha.toFixed(3)})`;
    }
  }

  burst(x: number, y: number, nx: number, ny: number, intensity: number): void {
    const n = Math.floor(4 + intensity * 10);
    const baseAngle = Math.atan2(ny, nx);
    for (let i = 0; i < n; i++) {
      const idx = this.count;
      this.count = (this.count + 1) % this.capacity;
      const angle = baseAngle + (Math.random() - 0.5) * 0.6;
      const speed = (80 + Math.random() * 180) * intensity;
      this.px[idx] = x;
      this.py[idx] = y;
      this.vx[idx] = Math.cos(angle) * speed;
      this.vy[idx] = Math.sin(angle) * speed;
      const life = 0.25 + Math.random() * 0.25;
      this.life[idx] = life;
      this.maxLife[idx] = life;
      this.size[idx] = 2 + Math.random() * 4;
    }
  }

  /**
   * Emisión RADIAL: cascotes en todas las direcciones, no un cono.
   *
   * `burst` abre un abanico estrecho alrededor de una normal, que es lo que
   * quiere una patada (sale hacia algún sitio). Un gol no tiene dirección: el
   * disco revienta contra la red. Reutilizar `burst` con una normal cualquiera
   * daría una explosión que apunta, y se nota.
   *
   * `drift` inclina el reparto hacia la dirección que llevaba el disco, para que
   * la explosión conserve algo del golpe sin dejar de ser radial.
   */
  spray(x: number, y: number, n: number, speed: number, driftX = 0, driftY = 0): void {
    for (let i = 0; i < n; i++) {
      const idx = this.count;
      this.count = (this.count + 1) % this.capacity;
      const angle = Math.random() * Math.PI * 2;
      const v = speed * (0.35 + Math.random() * 0.65);
      this.px[idx] = x;
      this.py[idx] = y;
      this.vx[idx] = Math.cos(angle) * v + driftX;
      this.vy[idx] = Math.sin(angle) * v + driftY;
      const life = 0.4 + Math.random() * 0.5;
      this.life[idx] = life;
      this.maxLife[idx] = life;
      this.size[idx] = 2 + Math.random() * 5;
    }
  }

  /** Apaga todo lo que hubiera en el aire (saque, cambio de mapa, fin de partido). */
  clear(): void {
    this.life.fill(0);
  }

  update(dt: number): void {
    for (let i = 0; i < this.capacity; i++) {
      if (this.life[i] <= 0) continue;
      this.life[i] -= dt;
      if (this.life[i] <= 0) continue;
      this.px[i] += this.vx[i] * dt;
      this.py[i] += this.vy[i] * dt;
      this.vx[i] *= 0.9;
      this.vy[i] *= 0.9;
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    for (let bin = 0; bin < BINS; bin++) {
      let any = false;
      for (let i = 0; i < this.capacity; i++) {
        if (this.life[i] <= 0) continue;
        const t = this.life[i] / this.maxLife[i];
        const binIdx = t >= 1 ? BINS - 1 : (t * BINS) | 0;
        if (binIdx !== bin) continue;
        if (!any) { ctx.beginPath(); any = true; }
        const s = this.size[i] * t;
        ctx.moveTo(this.px[i] + s, this.py[i]);
        ctx.arc(this.px[i], this.py[i], s, 0, Math.PI * 2);
      }
      if (!any) continue;
      ctx.fillStyle = this.binColors[bin];
      ctx.fill();
    }
  }
}

/* ==================== Estela del balón ==================== */

/** Muestras de la estela del balón. Menos que la de los discos: el balón va
 *  mucho más rápido, así que la misma cantidad daría un trazo larguísimo. */
export const BALL_TRAIL_SAMPLES = 14;

/**
 * Por debajo de esta fracción de la rapidez máxima, el balón no deja estela.
 *
 * Tiene que estar por encima de lo que da un toque sin carga (≈0,43 de la
 * máxima), o el balón iría siempre encendido y la estela dejaría de decir
 * "esto ha salido fuerte" para decir "hay un balón".
 */
export const BALL_TRAIL_FLOOR = 0.28;

/**
 * Cuánto se nota la estela del balón, de 0 a 1.
 *
 * La curva no es lineal a propósito (`^1.6`): repartida por igual, un pase
 * cualquiera ya salía medio encendido y el disparo a barra llena no destacaba.
 * Con la curva, la mitad del recorrido da un tercio del brillo y el último tramo
 * sube rápido — que es exactamente lo que se quiere señalar.
 *
 * Es una función aparte, y exportada, porque es la regla que decide si el efecto
 * significa algo: probarla no necesita ni lienzo ni navegador.
 */
export function ballTrailStrength(speed: number, maxSpeed: number): number {
  if (!(maxSpeed > 0) || !(speed > 0)) return 0;
  const r = speed / maxSpeed;
  if (r <= BALL_TRAIL_FLOOR) return 0;
  const t = Math.min(1, (r - BALL_TRAIL_FLOOR) / (1 - BALL_TRAIL_FLOOR));
  return Math.pow(t, 1.6);
}

/* ============ ACC-C · Ondas expansivas (gol y superpatada) ============ */

/** Ondas a la vez. Ocho sobran: un gol lanza dos y una superpatada una. */
export const MAX_RINGS = 8;

/**
 * A partir de qué intensidad una patada es una SUPERPATADA.
 *
 * `KickInfo.intensity` va de 0,4 (toque sin carga) a 1,0 (barra llena), así que
 * 0,9 es el último ~17 % del recorrido. Se eligió alto a propósito: si el aro
 * saliera en la mitad de las patadas dejaría de significar nada, y lo que se
 * quiere marcar es exactamente el disparo que costó aguantar la posesión hasta
 * el final.
 */
export const SUPER_KICK_INTENSITY = 0.9;

/**
 * Aros que crecen y se apagan.
 *
 * Los dos efectos que faltaban de la Fase C —la explosión de gol y la
 * superpatada— son **la misma figura** con distinto tamaño, color y duración: un
 * frente que sale del punto de impacto. Escribirlos por separado habría dado dos
 * copias del mismo bucle que se desincronizan a la primera corrección.
 *
 * Es deliberadamente pequeño: sin asignación por frame (arrays preasignados, el
 * color se guarda como cadena ya montada al lanzar) y un `stroke()` por aro
 * vivo. Con un aro y medio en pantalla en el peor caso, no hay nada que
 * optimizar aquí, y por eso tampoco se ha hecho.
 */
export class RingSystem {
  readonly capacity: number;
  private x: Float64Array;
  private y: Float64Array;
  private r0: Float64Array;
  private r1: Float64Array;
  private life: Float64Array;
  private maxLife: Float64Array;
  private width: Float64Array;
  /** Color base sin envolver, por aro. Vacío = ranura libre. */
  private rgb: string[];
  private next = 0;

  constructor(capacity = MAX_RINGS) {
    this.capacity = Math.max(1, capacity | 0);
    this.x = new Float64Array(this.capacity);
    this.y = new Float64Array(this.capacity);
    this.r0 = new Float64Array(this.capacity);
    this.r1 = new Float64Array(this.capacity);
    this.life = new Float64Array(this.capacity);
    this.maxLife = new Float64Array(this.capacity);
    this.width = new Float64Array(this.capacity);
    this.rgb = new Array(this.capacity).fill("");
  }

  /**
   * Lanza un aro que va de `from` a `to` píxeles de radio en `life` segundos.
   *
   * Se pisa la ranura más vieja cuando no quedan libres. Es lo correcto para
   * esto: si llegan nueve ondas a la vez, la que sobra es la primera, no la que
   * acaba de ocurrir — que es la que la persona está mirando.
   */
  spawn(x: number, y: number, from: number, to: number, life: number, rgb: string, width = 3): void {
    const i = this.next;
    this.next = (this.next + 1) % this.capacity;
    this.x[i] = x;
    this.y[i] = y;
    this.r0[i] = from;
    this.r1[i] = to;
    this.life[i] = Math.max(0.01, life);
    this.maxLife[i] = this.life[i];
    this.width[i] = width;
    this.rgb[i] = rgb;
  }

  clear(): void {
    this.life.fill(0);
    this.rgb.fill("");
  }

  update(dt: number): void {
    for (let i = 0; i < this.capacity; i++) {
      if (this.life[i] <= 0) continue;
      this.life[i] -= dt;
      if (this.life[i] <= 0) this.rgb[i] = "";
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    for (let i = 0; i < this.capacity; i++) {
      if (this.life[i] <= 0) continue;
      // 0 al nacer, 1 al morir.
      const t = 1 - this.life[i] / this.maxLife[i];
      // Se frena al final (cúbica invertida): un aro a velocidad constante
      // parece un círculo dibujado, no una onda.
      const ease = 1 - Math.pow(1 - t, 3);
      const r = this.r0[i] + (this.r1[i] - this.r0[i]) * ease;
      if (r <= 0.5) continue;
      const alpha = (1 - t) * 0.9;
      ctx.beginPath();
      ctx.arc(this.x[i], this.y[i], r, 0, Math.PI * 2);
      ctx.lineWidth = Math.max(0.5, this.width[i] * (1 - t));
      ctx.strokeStyle = `rgba(${this.rgb[i]},${alpha.toFixed(3)})`;
      ctx.stroke();
    }
  }
}

/* ==================== ACC-C · Estelas de movimiento ==================== */

/** Muestras guardadas por jugador. A 60 fps son ~0,4 s de estela. */
export const TRAIL_SAMPLES = 24;
/** Tope de jugadores con estela. Es la capacidad de una sala. */
export const TRAIL_MAX_PLAYERS = 8;
/** Bandas de opacidad. Cada una es UN `fill()`; más bandas = más suave y más caro. */
const TRAIL_BINS = 4;

/**
 * Estelas detrás de los discos.
 *
 * Es el cosmético con riesgo del plan: son partículas por jugador y por frame, y
 * el presupuesto de frame es lo que sostiene los 60 fps. Por eso está escrito
 * para que **el coste de dibujo no crezca con el número de jugadores**.
 *
 * El truco es el mismo que ya usa `ParticleSystem`, llevado al límite: en vez de
 * un `fill()` por muestra —que con 8 jugadores × 24 muestras son 192 llamadas
 * por frame—, todas las muestras del mismo EQUIPO y la misma banda de opacidad
 * entran en un solo camino y se rellenan de una vez. Eso deja el dibujo en
 * `2 equipos × 4 bandas = 8 fills` fijos, jueguen 2 personas u 8.
 *
 * Sin asignación en el camino caliente: anillos `Float64Array` preasignados y un
 * índice que gira. Lo único que se hace por frame es escribir una posición.
 */
export class TrailSystem {
  /** Longitud de la estela EN MUESTRAS. Configurable: es un rasgo del cosmético. */
  readonly samples: number;
  /** Anillos de posición: `[jugador * TRAIL_SAMPLES + muestra]`. */
  private px: Float64Array;
  private py: Float64Array;
  /** Cuántas muestras válidas tiene cada jugador (hasta TRAIL_SAMPLES). */
  private filled: Int32Array;
  /** Dónde se escribe la siguiente muestra de cada jugador. */
  private head: Int32Array;
  /** Equipo de cada jugador, para agrupar por color. -1 = sin estela. */
  private team: Int32Array;
  /**
   * Fuerza de la estela de cada slot, 0..1. Multiplica opacidad y grosor.
   *
   * Existe por la estela del BALÓN, que no es un adorno constante como la de los
   * discos: crece con la potencia del disparo y se apaga sola al frenar. Se
   * aplica con `globalAlpha` y no montando otra cadena `rgba(...)`, porque eso
   * último sería asignar memoria en el bucle de dibujo 60 veces por segundo.
   */
  private strength: Float64Array;
  private binColors: [string[], string[]];

  constructor(colors: [string, string] = ["255,46,99", "34,230,255"], samples = TRAIL_SAMPLES) {
    this.samples = Math.max(2, samples | 0);
    const n = TRAIL_MAX_PLAYERS * this.samples;
    this.px = new Float64Array(n);
    this.py = new Float64Array(n);
    this.filled = new Int32Array(TRAIL_MAX_PLAYERS);
    this.head = new Int32Array(TRAIL_MAX_PLAYERS);
    this.team = new Int32Array(TRAIL_MAX_PLAYERS).fill(-1);
    this.strength = new Float64Array(TRAIL_MAX_PLAYERS).fill(1);
    // Los colores se precalculan: construir una cadena `rgba(...)` por muestra y
    // por frame es basura que hay que recoger 60 veces por segundo.
    const build = (rgb: string): string[] => {
      const out: string[] = [];
      for (let b = 0; b < TRAIL_BINS; b++) out.push(`rgba(${rgb},${(((b + 0.5) / TRAIL_BINS) * 0.5).toFixed(3)})`);
      return out;
    };
    this.binColors = [build(colors[0]), build(colors[1])];
  }

  /** Olvida todas las estelas (cambio de mapa, saque, fin de partido). */
  clear(): void {
    this.filled.fill(0);
    this.head.fill(0);
    this.team.fill(-1);
    this.strength.fill(1);
  }

  /** Deja de dibujar la estela de un jugador (se fue, o no la tiene comprada). */
  disable(slot: number): void {
    if (slot < 0 || slot >= TRAIL_MAX_PLAYERS) return;
    this.filled[slot] = 0;
    this.team[slot] = -1;
  }

  /**
   * Añade la posición actual de un jugador. Una escritura, sin asignar nada.
   *
   * `maxStep` corta la estela cuando el disco **salta** en vez de moverse: un
   * saque, un reinicio, una reaparición. Sin esto, tras cada gol quedaría un
   * trazo cruzando la cancha entera desde donde estaba hasta el centro — y ese
   * trazo no es un fallo de dibujo, es la estela haciendo exactamente lo que se
   * le pide con datos que ya no son continuos.
   *
   * Va aquí y no en quien llama a propósito: detectar el salto es responsabilidad
   * de quien guarda el historial, y repartirla por los eventos del juego (gol,
   * saque, cambio de mapa, expulsión…) es garantizar que alguno se olvide.
   */
  sample(slot: number, team: 0 | 1, x: number, y: number, maxStep = 0, strength = 1): void {
    if (slot < 0 || slot >= TRAIL_MAX_PLAYERS) return;
    if (maxStep > 0 && this.filled[slot] > 0) {
      const prev = slot * this.samples + ((this.head[slot] - 1 + this.samples) % this.samples);
      const dx = x - this.px[prev];
      const dy = y - this.py[prev];
      if (dx * dx + dy * dy > maxStep * maxStep) this.filled[slot] = 0;
    }
    this.team[slot] = team;
    this.strength[slot] = strength < 0 ? 0 : strength > 1 ? 1 : strength;
    const base = slot * this.samples;
    const h = this.head[slot];
    this.px[base + h] = x;
    this.py[base + h] = y;
    this.head[slot] = (h + 1) % this.samples;
    if (this.filled[slot] < this.samples) this.filled[slot]++;
  }

  /**
   * Dibuja las estelas: un círculo por muestra, del más viejo y tenue al más
   * reciente y opaco.
   *
   * Hubo una versión "lista" que agrupaba todas las muestras del mismo equipo y
   * la misma banda de opacidad en un solo `fill()`, para que el número de
   * llamadas de dibujo no creciera con los jugadores. Sonaba mejor y **no lo
   * era**: medida contra esta, con 8 jugadores salió 0,9 ms frente a 0,7 ms con
   * 24 muestras, y empate exacto (2,1 ms) con 72. El ahorro en llamadas se lo
   * comía el bucle extra que hacía falta para agruparlas.
   *
   * Se quedó la simple. La medición está en `bench/trails.html` y se puede
   * repetir; lo que no se puede es volver a añadir la complicada sin volver a
   * medir. Ver docs/PLAN-TIENDA.md · Fase C.
   */
  draw(ctx: CanvasRenderingContext2D, radius: number): void {
    for (let slot = 0; slot < TRAIL_MAX_PLAYERS; slot++) {
      const equipo = this.team[slot];
      if (equipo < 0) continue;
      const fuerza = this.strength[slot];
      // Por debajo de esto no se distingue de no dibujar nada, y saltárselo
      // ahorra el bucle entero de un balón parado (que es casi todo el partido).
      if (fuerza < 0.02) continue;
      if (fuerza < 1) ctx.globalAlpha = fuerza;
      const n = this.filled[slot];
      const base = slot * this.samples;
      const h = this.head[slot];
      for (let k = 0; k < n; k++) {
        const idx = base + ((h - n + k + this.samples * 2) % this.samples);
        const t = (k + 1) / n;
        const s = radius * 0.55 * t;
        if (s < 0.4) continue;
        ctx.beginPath();
        ctx.arc(this.px[idx], this.py[idx], s, 0, Math.PI * 2);
        ctx.fillStyle = this.binColors[equipo][t >= 1 ? TRAIL_BINS - 1 : (t * TRAIL_BINS) | 0];
        ctx.fill();
      }
      if (fuerza < 1) ctx.globalAlpha = 1;
    }
  }
}
